import * as React from 'react'
import {
  FunctionComponent,
  memo,
  useState,
  useEffect,
  useCallback,
} from 'react'
import { Formik, FormikActions } from 'formik'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'

import Modal from '../../components/Modal'
import Input from '../../components/Input'
import Button from '../../components/Button'
import Select from '../../components/Select'
import DropdownSelect from '../../components/DropdownSelect'
import useThunkDispatch from '../../common/useThunkDispatch'
import {
  updateLocation,
  deleteLocation,
  fetchLocationAssignableUsers,
  fetchLocation,
  createLocation,
  fetchLocations,
  fetchLocationWithItems,
} from '../locationsActions'
import {
  accountsDictSelector,
  detailedAccountSelector,
} from '../accountsSelectors'
import {
  ILocation,
  LocationTypes,
  LOCATION_TYPE_LABELS,
  ILocationFormValues,
  IUserOption,
  ILocationUpdateAttributes,
  ILocationCreateAttributes,
} from '../../types/location'
import { managerBranchSelector } from '../../manager/managerSelectors'
import { currentUserSelector } from '../../auth/authSelectors'
import { IAccount } from '../../types/user'
import { IHash } from '../../types/common'

const resolveAccountData = (
  detailedAccount: IAccount | undefined,
  accounts: IHash<IAccount>,
  effectiveAccountId: number | undefined,
  currentUserAccount: IAccount | undefined,
): IAccount | undefined => {
  if (detailedAccount?.id === effectiveAccountId) return detailedAccount
  if (effectiveAccountId)
    return accounts[effectiveAccountId] ?? currentUserAccount ?? null
  return undefined
}

interface IOwnProps {
  branchId?: number
  accountId?: number
}

const LocationForm: FunctionComponent<IOwnProps> = ({
  branchId,
  accountId,
}) => {
  const navigate = useNavigate()
  const routerLocation = useLocation()
  const fallbackBranch = useSelector(managerBranchSelector)
  const currentUser = useSelector(currentUserSelector)

  // Read optional params for client-scoped routes
  const {
    locationId,
    accountId: accountIdParam,
    branchId: branchIdParam,
  } = useParams<{
    locationId?: string
    accountId?: string
    branchId?: string
  }>()

  const paramAccountId = accountIdParam
    ? parseInt(accountIdParam, 10)
    : undefined
  const paramBranchId = branchIdParam ? parseInt(branchIdParam, 10) : undefined

  const effectiveBranchId = paramBranchId ?? branchId ?? fallbackBranch?.id
  const effectiveAccountId =
    paramAccountId ?? accountId ?? currentUser?.account?.id

  const dispatch = useThunkDispatch()
  const accounts = useSelector(accountsDictSelector)
  const detailedAccount = useSelector(detailedAccountSelector)
  const accountData = resolveAccountData(
    detailedAccount,
    accounts,
    effectiveAccountId,
    currentUser?.account,
  )

  const effectiveBranch =
    accountData?.branches?.find((b) => b.id === effectiveBranchId) ??
    fallbackBranch
  const truckEnabled =
    accountData?.truckInventoryFeature && effectiveBranch?.truckInventoryFeature
  const binEnabled =
    accountData?.binInventoryFeature && effectiveBranch?.binInventoryFeature
  const showLocationType = truckEnabled || binEnabled
  const locationTypeEditable = truckEnabled && binEnabled

  const [assignableUsers, setAssignableUsers] = useState<IUserOption[]>([])
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false)
  const [locationsList, setLocationsList] = useState<ILocation[]>([])
  const [loadingLocationsList, setLoadingLocationsList] =
    useState<boolean>(false)
  const [loadingDelete, setLoadingDelete] = useState(false)
  const [location, setLocation] = useState<ILocation | null>(null)
  const [loadingLocation, setLoadingLocation] = useState<boolean>(true)
  const [formMode, setFormMode] = useState<'edit' | 'new'>('new')
  const [hasItems, setHasItems] = useState<boolean>(false)

  const onClose = () => {
    const state = routerLocation.state
    if (paramAccountId && paramBranchId) {
      navigate(
        `/clients/${paramAccountId}/branches/${paramBranchId}/locations`,
        { state },
      )
    } else {
      navigate('/locations', { state })
    }
  }

  useEffect(() => {
    const loadUsers = async () => {
      setLoadingUsers(true)
      try {
        if (!effectiveBranchId) {
          setAssignableUsers([])
          return
        }
        const usersList = await dispatch(
          fetchLocationAssignableUsers(effectiveBranchId, effectiveAccountId),
        )

        setAssignableUsers(usersList)
      } catch (error) {
        console.error('Error loading users:', error)
        setAssignableUsers([])
      } finally {
        setLoadingUsers(false)
      }
    }

    loadUsers()
  }, [effectiveBranchId, effectiveAccountId, dispatch])

  useEffect(() => {
    const loadLocationsList = async () => {
      if (!effectiveAccountId) {
        setLocationsList([])
        return
      }
      setLoadingLocationsList(true)
      try {
        const locs = await dispatch(
          fetchLocations(effectiveAccountId, effectiveBranchId),
        )
        setLocationsList(locs || [])
      } catch (error) {
        console.error('Error loading locations list:', error)
        setLocationsList([])
      } finally {
        setLoadingLocationsList(false)
      }
    }

    loadLocationsList()
  }, [effectiveAccountId, effectiveBranchId, dispatch])

  useEffect(() => {
    const loadLocation = async () => {
      if (locationId) {
        setFormMode('edit')
        setLoadingLocation(true)
        try {
          const fetchedLocation = await dispatch(
            fetchLocation(parseInt(locationId, 10)),
          )
          setLocation(fetchedLocation)
        } catch (error) {
          console.error('Error loading location:', error)
          setLocation(null)
        } finally {
          setLoadingLocation(false)
        }
      } else {
        setFormMode('new')
        let defaultLocationType = LocationTypes.TRUCK
        if (binEnabled) {
          defaultLocationType = LocationTypes.BIN
        }

        setLocation({
          code: '',
          description: '',
          locationType: defaultLocationType,
          userId: null,
          id: 0,
          accountId: 0,
          createdAt: '',
          updatedAt: '',
        })
        setLoadingLocation(false)
        setHasItems(false)
      }
    }

    loadLocation()
  }, [locationId, dispatch, truckEnabled, binEnabled])

  // Check if the location has items to disable delete button
  useEffect(() => {
    const loadItems = async () => {
      if (!locationId) {
        setHasItems(false)
        return
      }
      try {
        const locWithItems = await dispatch(
          fetchLocationWithItems(parseInt(locationId, 10)),
        )
        const items = locWithItems?.locationItems || []
        setHasItems(items.length > 0)
      } catch (e) {
        console.error('Error checking location items:', e)
        // On error, be conservative and disable deletion to avoid accidental delete attempts
        setHasItems(true)
      }
    }

    loadItems()
  }, [locationId, dispatch])

  const handleCreate = async (
    values: ILocationFormValues,
    actions: FormikActions<ILocationFormValues>,
  ) => {
    actions.setSubmitting(true)

    try {
      const createAttrs: ILocationCreateAttributes = {
        code: values.code,
        description: values.description,
        locationType: values.locationType,
        accountId: effectiveAccountId!,
        userId: values.userId,
        branchIds: effectiveBranchId ? [effectiveBranchId] : [],
        cycleCountStartDate: values.cycleCountStartDate || null,
        cycleCountFrequencyDays: values.cycleCountFrequencyDays
          ? parseInt(values.cycleCountFrequencyDays, 10)
          : null,
      }

      const errors = await dispatch(createLocation(createAttrs))
      if (!errors) onClose()
      else {
        actions.setErrors(errors)
      }
    } catch (e) {
      alert(e && e.message ? e.message : 'Server Error')
    }
    actions.setSubmitting(false)
  }

  const handleUpdate = async (
    values: ILocationFormValues,
    actions: FormikActions<ILocationFormValues>,
  ) => {
    if (!location) return
    actions.setSubmitting(true)

    try {
      const updateAttrs: ILocationUpdateAttributes = {
        code: values.code,
        description: values.description,
        locationType: values.locationType,
        accountId: effectiveAccountId!,
        userId: values.userId,
        branchIds: effectiveBranchId ? [effectiveBranchId] : [],
        cycleCountStartDate: values.cycleCountStartDate || null,
        cycleCountFrequencyDays: values.cycleCountFrequencyDays
          ? parseInt(values.cycleCountFrequencyDays, 10)
          : null,
      }

      const errors = await dispatch(
        updateLocation((location as ILocation).id, updateAttrs),
      )
      if (!errors) onClose()
      else {
        actions.setErrors(errors)
      }
    } catch (e) {
      alert(e && e.message ? e.message : 'Server Error')
    }
    actions.setSubmitting(false)
  }

  const handleDelete = useCallback(async () => {
    if (!location) return
    if (
      window.confirm(
        `You are about to delete the location ${location.code} with their items. Are you sure?`,
      )
    ) {
      try {
        setLoadingDelete(true)
        await dispatch(deleteLocation((location as ILocation).id))
        onClose()
      } catch (e) {
        alert(e)
      }
      setLoadingDelete(false)
    }
  }, [location, dispatch, onClose])

  if (loadingLocation) {
    return (
      <Modal onClose={() => onClose()} title="Edit Location">
        <div className="form">
          <p>Loading location data...</p>
        </div>
      </Modal>
    )
  }

  if (locationId && !location) {
    return (
      <Modal onClose={() => onClose()} title="Error">
        <div className="form">
          <p>Could not load location data.</p>
          <div className="form_controls">
            <div className="form_controls-container">
              <Button onClick={() => onClose()} color="default-link">
                Close
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      onClose={() => onClose()}
      title={location?.id ? 'Edit Location' : 'Add location'}
    >
      <Formik<ILocationFormValues>
        initialValues={{
          code: location?.code || '',
          description: location?.description || '',
          locationType:
            location?.locationType ||
            (binEnabled ? LocationTypes.BIN : LocationTypes.TRUCK),
          userId: location?.user?.id || null,
          cycleCountStartDate: location?.cycleCountStartDate || '',
          cycleCountFrequencyDays:
            location?.cycleCountFrequencyDays?.toString() || '',
        }}
        onSubmit={formMode === 'edit' ? handleUpdate : handleCreate}
        render={({
          values,
          errors,
          handleChange,
          handleSubmit,
          isSubmitting,
          dirty,
          setFieldValue,
        }) => (
          <form className="form" onSubmit={handleSubmit}>
            {showLocationType && (
              <div className="form_group">
                <DropdownSelect<string>
                  label="Location type"
                  value={values.locationType}
                  options={[
                    {
                      label: LOCATION_TYPE_LABELS[LocationTypes.BIN],
                      value: LocationTypes.BIN,
                    },
                    {
                      label: LOCATION_TYPE_LABELS[LocationTypes.TRUCK],
                      value: LocationTypes.TRUCK,
                    },
                  ]}
                  onChange={(value) => setFieldValue('locationType', value)}
                  disabled={!locationTypeEditable}
                  className="-md"
                  dropdownClassName="input"
                  dropdownValueClassName="input_input -flex white-bg"
                />
              </div>
            )}
            <div className="form_group">
              <Input
                onChange={handleChange('code')}
                name="code"
                label="Location code"
                value={values.code}
                error={errors.code?.toString()}
              />
            </div>
            <div className="form_group">
              <Select<number>
                value={
                  assignableUsers.find((u) => u.value === values.userId) || null
                }
                options={assignableUsers.map((u) => ({
                  ...u,
                  isDisabled:
                    values.locationType === LocationTypes.TRUCK &&
                    locationsList.some(
                      (loc) =>
                        loc.locationType === LocationTypes.TRUCK &&
                        loc.user &&
                        loc.user.id === u.value &&
                        loc.id !== (location?.id || 0),
                    ),
                }))}
                onChange={(option: IUserOption | null) =>
                  setFieldValue('userId', option ? option.value : null)
                }
                isClearable
                isDisabled={loadingUsers || loadingLocationsList}
                isLoading={loadingUsers || loadingLocationsList}
                classNamePrefix="select"
                placeholder="Select User"
                label="User"
              />
            </div>
            <div className="form_group">
              <Input
                onChange={handleChange('description')}
                name="description"
                label="Description"
                value={values.description}
              />
            </div>
            {values.locationType === LocationTypes.BIN && (
              <div className="form_group" style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 3 }}>
                  <Input
                    type="date"
                    onChange={handleChange('cycleCountStartDate')}
                    name="cycleCountStartDate"
                    label="Start Cycle Count From"
                    value={values.cycleCountStartDate}
                  />
                </div>
                <div style={{ flex: 2 }}>
                  <Input
                    type="number"
                    onChange={handleChange('cycleCountFrequencyDays')}
                    name="cycleCountFrequencyDays"
                    label="Count Frequency, Days"
                    value={values.cycleCountFrequencyDays}
                    placeholder="0"
                    disabled={!values.cycleCountStartDate}
                  />
                </div>
              </div>
            )}

            <div className="form_controls">
              <div className="form_controls-container">
                {formMode === 'edit' && (
                  <>
                    <Button
                      onClick={handleDelete}
                      color={hasItems ? 'default-link' : 'red-link'}
                      loading={loadingDelete}
                      disabled={hasItems || loadingDelete}
                      className="form_control -left"
                    >
                      Delete
                    </Button>
                    {hasItems && (
                      <div
                        className="form_controls-hint"
                        style={{ marginLeft: 8 }}
                      >
                        Cannot delete a location that has items
                      </div>
                    )}
                  </>
                )}
                <Button
                  disabled={isSubmitting}
                  onClick={() => onClose()}
                  loading={loadingDelete}
                  color="default-link"
                  className="form_control -size -right"
                >
                  Cancel
                </Button>
                <Button
                  loading={isSubmitting || loadingDelete}
                  disabled={isSubmitting || !dirty || !values.code.trim()}
                  type="submit"
                  color="success"
                  className="form_control -size"
                >
                  Save
                </Button>
              </div>
            </div>
          </form>
        )}
      />
    </Modal>
  )
}

export default memo(LocationForm)
